import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ArrowLeft, Copy, Upload, Trash2, Download, Trophy, RefreshCcw, Save, ExternalLink, Share2, MessageCircle, Mail, Link as LinkIcon
} from "lucide-react";
import { getTenantSlugFromPath } from "@/lib/tenant";

const FIELDS = [
  { key: "name",             label: "Name",                hint: "Entrant's full name" },
  { key: "email",            label: "Email",               hint: "Entrant's email address" },
  { key: "phone",            label: "Phone Number",        hint: "Entrant's mobile/phone" },
  { key: "marketing_opt_in", label: "Marketing Opt-in",    hint: "Checkbox to collect leads", noRequired: true },
  { key: "custom_question",  label: "Custom Question",     hint: "Free-text question (set its label below)", hasLabel: true },
];

export default function CompetitionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tenantSlug = getTenantSlugFromPath();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [comp, setComp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/competitions/${id}`);
      setComp(res.data);
    } catch (e) {
      toast.error("Failed to load competition");
      navigate("/admin/competitions");
    }
    setLoading(false);
  };

  const loadEntries = async () => {
    setEntriesLoading(true);
    try {
      const res = await axios.get(`${API}/competitions/${id}/entries`);
      setEntries(res.data || []);
    } catch (e) {
      toast.error("Failed to load entries");
    }
    setEntriesLoading(false);
  };

  useEffect(() => { load(); loadEntries(); }, [id]);

  const update = (patch) => setComp((c) => ({ ...c, ...patch }));
  const updateField = (key, patch) => setComp((c) => ({
    ...c,
    field_config: { ...c.field_config, [key]: { ...(c.field_config?.[key] || {}), ...patch } },
  }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/competitions/${id}`, {
        name: comp.name,
        slug: comp.slug,
        description: comp.description,
        prize: comp.prize,
        terms_label: comp.terms_label,
        thank_you_message: comp.thank_you_message,
        start_date: comp.start_date,
        end_date: comp.end_date,
        status: comp.status,
        field_config: comp.field_config,
        email_notifications_enabled: comp.email_notifications_enabled,
      });
      setComp(res.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/competitions/${id}/banner`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update({ banner_image_url: res.data.url });
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const remove = async () => {
    if (!window.confirm("Delete this competition and all entries? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/competitions/${id}`);
      toast.success("Deleted");
      navigate("/admin/competitions");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const pickWinner = async () => {
    setPicking(true);
    try {
      const res = await axios.post(`${API}/competitions/${id}/pick-winner`);
      toast.success(`Winner: ${res.data.winner.name}`);
      await load();
      await loadEntries();
      setPickOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pick winner");
    }
    setPicking(false);
  };

  const clearWinner = async () => {
    try {
      await axios.post(`${API}/competitions/${id}/clear-winner`);
      toast.success("Winner cleared");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/competitions/${id}/entries/${entryId}`);
      toast.success("Entry deleted");
      await loadEntries();
      await load();
    } catch (e) {
      toast.error("Failed to delete entry");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/competitions/${id}/entries.csv`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${comp.slug}-entries.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (loading || !comp) {
    return <p className="text-sm text-muted-foreground" data-testid="competition-edit-loading">Loading…</p>;
  }

  const publicUrl = `${window.location.origin}/${tenantSlug}/competition/${comp.slug}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied!"); }
    catch { toast.error("Couldn't copy"); }
  };
  const shareText = encodeURIComponent(`Enter ${comp.name} ✨ ${publicUrl}`);
  const whatsappShare = `https://wa.me/?text=${shareText}`;
  const emailShare = `mailto:?subject=${encodeURIComponent(`Enter ${comp.name}`)}&body=${shareText}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: comp.name, text: `Enter ${comp.name}`, url: publicUrl }); }
      catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const winner = entries.find((e) => e.id === comp.winner_entry_id);

  return (
    <div className="space-y-5" data-testid="competition-edit-page">
      <Toaster position="top-center" />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/competitions">
            <Button variant="ghost" size="sm" className="rounded-xl" data-testid="back-btn">
              <ArrowLeft size={14} className="mr-1.5" /> Competitions
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-semibold" style={{ fontFamily: "Playfair Display" }}>{comp.name}</h1>
            <p className="text-[11px] text-muted-foreground">/{tenantSlug}/competition/{comp.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setShareOpen(true)} data-testid="share-btn">
            <Share2 size={14} className="mr-1.5" /> Share
          </Button>
          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => window.open(publicUrl, "_blank")} data-testid="preview-btn">
            <ExternalLink size={14} className="mr-1.5" /> Preview
          </Button>
          <Button size="sm" className="rounded-xl" onClick={save} disabled={saving} data-testid="save-btn">
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="setup" className="w-full">
        <TabsList>
          <TabsTrigger value="setup" data-testid="tab-setup">Setup</TabsTrigger>
          <TabsTrigger value="fields" data-testid="tab-fields">Form Fields</TabsTrigger>
          <TabsTrigger value="entries" data-testid="tab-entries">
            Entries <Badge variant="outline" className="ml-2 text-[10px]">{entries.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="danger" data-testid="tab-danger">Danger Zone</TabsTrigger>
        </TabsList>

        {/* ── Setup ── */}
        <TabsContent value="setup" className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={comp.name} onChange={(e) => update({ name: e.target.value })} className="mt-1" data-testid="field-name" />
              </div>
              <div>
                <Label>URL Slug</Label>
                <Input
                  value={comp.slug}
                  onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                  className="mt-1"
                  data-testid="field-slug"
                />
                <p className="text-[11px] text-muted-foreground mt-1">{`${window.location.origin}/${tenantSlug}/competition/${comp.slug}`}</p>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={comp.description}
                onChange={(e) => update({ description: e.target.value })}
                rows={3}
                className="mt-1"
                placeholder="Tell entrants what the competition is about"
                data-testid="field-description"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Prize</Label>
                <Input
                  value={comp.prize}
                  onChange={(e) => update({ prize: e.target.value })}
                  className="mt-1"
                  placeholder="e.g. Luxury manicure + £50 voucher"
                  data-testid="field-prize"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={comp.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger className="mt-1" data-testid="field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (hidden)</SelectItem>
                    <SelectItem value="active">Active (accepting entries)</SelectItem>
                    <SelectItem value="closed">Closed (read-only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Start Date (optional)</Label>
                <Input type="date" value={comp.start_date || ""} onChange={(e) => update({ start_date: e.target.value })} className="mt-1" data-testid="field-start-date" />
              </div>
              <div>
                <Label>End Date (optional)</Label>
                <Input type="date" value={comp.end_date || ""} onChange={(e) => update({ end_date: e.target.value })} className="mt-1" data-testid="field-end-date" />
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Banner Image</h3>
                <p className="text-xs text-muted-foreground">Shown at the top of the public entry form.</p>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" data-testid="banner-input" />
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => fileInputRef.current?.click()} data-testid="upload-banner-btn">
                <Upload size={14} className="mr-1.5" /> Upload
              </Button>
            </div>
            {comp.banner_image_url ? (
              <img src={comp.banner_image_url} alt="banner" className="w-full max-h-48 object-cover rounded-xl" />
            ) : (
              <div className="border border-dashed rounded-xl p-8 text-center text-xs text-muted-foreground">
                No banner uploaded
              </div>
            )}
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="text-sm font-semibold">Submission Experience</h3>
            <div>
              <Label>Terms Checkbox Label</Label>
              <Input
                value={comp.terms_label}
                onChange={(e) => update({ terms_label: e.target.value })}
                className="mt-1"
                placeholder="I accept the terms & conditions"
                data-testid="field-terms-label"
              />
            </div>
            <div>
              <Label>Thank-You Message</Label>
              <Textarea
                value={comp.thank_you_message}
                onChange={(e) => update({ thank_you_message: e.target.value })}
                rows={3}
                className="mt-1"
                placeholder="Shown after a successful submission"
                data-testid="field-thank-you"
              />
            </div>
            <div className="flex items-center justify-between bg-muted/40 p-3 rounded-xl">
              <div>
                <Label className="cursor-pointer">Email Notifications</Label>
                <p className="text-[11px] text-muted-foreground">Send confirmation to entrants and notify the salon on every new entry.</p>
              </div>
              <Switch
                checked={!!comp.email_notifications_enabled}
                onCheckedChange={(v) => update({ email_notifications_enabled: v })}
                data-testid="toggle-email-notifications"
              />
            </div>
          </Card>
        </TabsContent>

        {/* ── Form Fields ── */}
        <TabsContent value="fields" className="space-y-4">
          <Card className="p-5 space-y-3">
            <div>
              <h3 className="text-sm font-semibold">Form Fields</h3>
              <p className="text-xs text-muted-foreground">Decide which fields are visible and which are required.</p>
            </div>
            <div className="space-y-2">
              {FIELDS.map((f) => {
                const cfg = comp.field_config?.[f.key] || { visible: true, required: false, label: f.label };
                return (
                  <div key={f.key} className="border rounded-xl p-3 space-y-2" data-testid={`field-row-${f.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{f.label}</p>
                        <p className="text-[11px] text-muted-foreground">{f.hint}</p>
                      </div>
                      <div className="flex items-center gap-4 pt-1">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <Switch
                            checked={!!cfg.visible}
                            onCheckedChange={(v) => updateField(f.key, { visible: v })}
                            data-testid={`visible-${f.key}`}
                          />
                          <span>Visible</span>
                        </label>
                        {!f.noRequired && (
                          <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${cfg.visible ? "" : "opacity-40"}`}>
                            <Switch
                              checked={!!cfg.required}
                              disabled={!cfg.visible}
                              onCheckedChange={(v) => updateField(f.key, { required: v })}
                              data-testid={`required-${f.key}`}
                            />
                            <span>Required</span>
                          </label>
                        )}
                      </div>
                    </div>
                    {f.hasLabel && cfg.visible && (
                      <Input
                        value={cfg.label}
                        onChange={(e) => updateField(f.key, { label: e.target.value })}
                        placeholder="Custom question label"
                        className="text-xs"
                        data-testid={`label-${f.key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* ── Entries ── */}
        <TabsContent value="entries" className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-sm font-semibold">Entries</h3>
                <p className="text-xs text-muted-foreground">{entries.length} total · sorted by latest</p>
                {winner && (
                  <p className="text-xs mt-1 inline-flex items-center gap-1 text-emerald-700 font-medium">
                    <Trophy size={12} /> Winner: {winner.name} ({winner.email})
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {comp.winner_entry_id ? (
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={clearWinner} data-testid="clear-winner-btn">
                    Clear Winner
                  </Button>
                ) : null}
                <Button
                  variant="outline" size="sm" className="rounded-xl"
                  onClick={() => setPickOpen(true)}
                  disabled={entries.length === 0}
                  data-testid="pick-winner-btn"
                >
                  <Trophy size={14} className="mr-1.5" /> Pick Winner
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={exportCsv} disabled={entries.length === 0} data-testid="export-csv-btn">
                  <Download size={14} className="mr-1.5" /> Export CSV
                </Button>
                <Button variant="ghost" size="sm" className="rounded-xl" onClick={loadEntries} data-testid="refresh-entries-btn">
                  <RefreshCcw size={14} />
                </Button>
              </div>
            </div>

            {entriesLoading ? (
              <p className="text-xs text-muted-foreground">Loading entries…</p>
            ) : entries.length === 0 ? (
              <div className="border border-dashed rounded-xl p-8 text-center text-xs text-muted-foreground" data-testid="entries-empty">
                No entries yet. Share the public link to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                    <tr>
                      <th className="py-2 pr-3">Submitted</th>
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Phone</th>
                      <th className="py-2 pr-3">Opt-in</th>
                      <th className="py-2 pr-3">Answer</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const isWinner = comp.winner_entry_id === e.id;
                      return (
                        <tr key={e.id} className={`border-b hover:bg-muted/30 ${isWinner ? "bg-emerald-50/50" : ""}`} data-testid={`entry-row-${e.id}`}>
                          <td className="py-2 pr-3 whitespace-nowrap text-[11px] text-muted-foreground">
                            {new Date(e.created_at).toLocaleString()}
                          </td>
                          <td className="py-2 pr-3 font-medium">
                            {isWinner && <Trophy size={11} className="inline mr-1 text-emerald-600" />}
                            {e.name}
                          </td>
                          <td className="py-2 pr-3">{e.email}</td>
                          <td className="py-2 pr-3">{e.phone}</td>
                          <td className="py-2 pr-3">{e.marketing_opt_in ? "Yes" : "No"}</td>
                          <td className="py-2 pr-3 max-w-[200px] truncate" title={e.custom_answer}>{e.custom_answer || "—"}</td>
                          <td className="py-2 pr-3">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteEntry(e.id)} data-testid={`delete-entry-${e.id}`}>
                              <Trash2 size={12} className="text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ── Danger Zone ── */}
        <TabsContent value="danger" className="space-y-4">
          <Card className="p-5 border-destructive/30">
            <h3 className="text-sm font-semibold text-destructive">Delete Competition</h3>
            <p className="text-xs text-muted-foreground mt-1">This permanently deletes the competition and all entries. This cannot be undone.</p>
            <Button variant="destructive" size="sm" className="rounded-xl mt-3" onClick={remove} data-testid="delete-competition-btn">
              <Trash2 size={14} className="mr-1.5" /> Delete Competition
            </Button>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pick Winner dialog */}
      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pick a Winner 🎲</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            A random entry will be chosen from {entries.length} {entries.length === 1 ? "entry" : "entries"}.
            {comp.winner_entry_id ? " This will replace the current winner." : ""}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={pickWinner} disabled={picking} className="rounded-xl" data-testid="confirm-pick-winner">
              {picking ? "Picking…" : "Pick Winner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Competition</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2 p-2 bg-muted/40 rounded-xl">
              <code className="text-[11px] flex-1 truncate" data-testid="share-url">{publicUrl}</code>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={copyLink} data-testid="share-copy-btn">
                <Copy size={12} className="mr-1" /> Copy
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <a href={whatsappShare} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp">
                <Button variant="outline" className="w-full rounded-xl">
                  <MessageCircle size={14} className="mr-1.5" /> WhatsApp
                </Button>
              </a>
              <a href={emailShare} data-testid="share-email">
                <Button variant="outline" className="w-full rounded-xl">
                  <Mail size={14} className="mr-1.5" /> Email
                </Button>
              </a>
              <Button variant="outline" className="rounded-xl" onClick={nativeShare} data-testid="share-native">
                <LinkIcon size={14} className="mr-1.5" /> Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
